import React, { useEffect } from 'react'
import { Col, Row } from 'reactstrap'
import { FormRow, PropertyRenderer } from '@/components/changeRequests'
import context from '@/context'
import { InfoContent, InfoLabel, InfoRow } from '@/components/InfoRow'
import Resx from '@/components/Resx'
import { FieldSet } from './helpers/fieldSet'
import { useCoupledProps } from '@/components/changeRequests/useCoupledProps'
import { PermissionSectionProvider } from '@/components/changeRequests/institutionPermissionProvider'
import { institutionPermissionTypes } from '@/enums/institutionPermissionTypes'
import { usePropertyReader } from '@/hooks/usePropertyReader'
import { activeTaskSelector } from '@/components/changeRequests/selectors'
import { useSelector } from 'react-redux'
import { usePropertySetter } from '@/hooks/usePropertySetter'
import { isNullOrEmptyString } from '@progress/kendo-data-query/dist/npm/utils'

export function CompanyBasicDetails () {
  const { onChange: onStatusChange, isChanged: statusChanged } = useCoupledProps('status', ['changeDate', 'approvalNumber'])
  const { value: companyType, onChange: onTypeChange } = useCoupledProps('companyType', ['subType'])
  const { value: dealsWithBank, onChange: onDealsWithBankChange, isChanged: dealsWithBankChanged } = useCoupledProps('dealsWithBank',
    ['dealsWithBankApprovalDate', 'dealsWithBankValidFrom', 'dealsWithBankChangeNumber', 'dealsWithBankChangeDate'])
  const [minimumInstitutions] = usePropertyReader(['minimumInstitutionsOverride'])
  const [approvalNumber] = usePropertyReader(['approvalNumber'], 'originalData')

  const activeTask = useSelector(activeTaskSelector) || context.isReportApp()
  const isAssignMergedCompaniesTask = activeTask && activeTask.type && activeTask.type.code === 'AssignMergedCompanies'
  const hasMinInstitutionsOverride = minimumInstitutions !== null && minimumInstitutions !== undefined
  const isManageMergedCompaniesTask = activeTask && activeTask.type && activeTask.type.code === 'ManageMergedCompanies'
  const isApproveWorkDoneTask = activeTask && activeTask.type && activeTask.type.code === 'ApproveWorkDone'

  const [mergedCompanyCountDB] = usePropertyReader(['mergedCompanyCount'])
  let [mergedCompanyCountAssign] = usePropertyReader(['mergedCompanies'])
  let [mergedCompanyCountNew] = usePropertyReader(['mergedCompanyCountNew'])

  const mergedCompanyCountSetter = usePropertySetter('mergedCompanyCountNew',
    (prop, value) => (prop || 0) === (value || 0))

  const newMergedCompanyCount = (isAssignMergedCompaniesTask || isApproveWorkDoneTask)
    ? (mergedCompanyCountDB || 0) + (Array.isArray(mergedCompanyCountAssign) ? mergedCompanyCountAssign.length : 0)
    : 0

  useEffect(() => {
    // Call the setter only if necessary
    if (isAssignMergedCompaniesTask || isApproveWorkDoneTask) {
      mergedCompanyCountSetter(newMergedCompanyCount)
    }
  }, [activeTask, isApproveWorkDoneTask, isAssignMergedCompaniesTask, mergedCompanyCountSetter, newMergedCompanyCount])

  return (
    <Row>
      <Col lg='6'>
        <PermissionSectionProvider institutionPermission={institutionPermissionTypes.enum.CompanyStatus}>
          <FieldSet className='state-history'>
            <FormRow label='Common.Status' name='status' inputType='lookup' dataSource={context.cache.getCompanyStates} onChange={onStatusChange} clientSide isCodeList required />
            <FormRow label='Common.ChangeDate' name='changeDate' inputType='date' required readonly={!statusChanged} />
            <FormRow label='Common.ApprovalNumber' name='approvalNumber' required readonly={!(statusChanged || isNullOrEmptyString(approvalNumber))} inputProps={{ maxLength: 32 }} />
          </FieldSet>
        </PermissionSectionProvider>
        <PermissionSectionProvider institutionPermission={institutionPermissionTypes.enum.CompanyType}>
          <FieldSet className='company-type' style={{ marginTop: 10 }}>
            <FormRow label='Common.Type'
              name='type'
              inputType='lookup'
              dataSource={context.cache.getCompanyTypes}
              onChange={onTypeChange}
              clientSide
              isCodeList
              required />
            <FormRow label='Common.Subtype' name='subtype' inputType='lookup' dataSource={context.cache.getCompanySubtypes} clientSide isCodeList filter={companyType} />
            <FormRow label='Common.ApprovalDate' name='companyTypeChangeApprovalDate' inputType='date' />
            <FormRow label='Common.ApprovalNumber' name='companyTypeChangeApprovalNumber' />
          </FieldSet>
        </PermissionSectionProvider>
        <PermissionSectionProvider institutionPermission={institutionPermissionTypes.enum.CompanyDetail}>
          <FieldSet>
            <FormRow label='Institutions.CompanyClass' name='class' inputType='lookup'
              dataSource={context.cache.getCompanyClasses} clientSide isCodeList required readonly={activeTask && !isManageMergedCompaniesTask && !isAssignMergedCompaniesTask && !isApproveWorkDoneTask} />
            <FormRow label='Institutions.MinimumInstitutionsMerge' name={hasMinInstitutionsOverride ? 'minimumInstitutionsOverride' : 'class.minimumInstitutions'} inputType='number' inputProps={{ min: 0 }} readonly={(activeTask && !isAssignMergedCompaniesTask && !isApproveWorkDoneTask)} />
            <FormRow label='Institutions.MergedCompanyCount' name={mergedCompanyCountNew ? 'mergedCompanyCountNew' : 'mergedCompanyCount'} inputType='number' readonly={(activeTask && !isAssignMergedCompaniesTask && !isApproveWorkDoneTask) || (mergedCompanyCountDB === newMergedCompanyCount)} />
          </FieldSet>
          <FormRow label='Institutions.NumberOfEmployees' name='numberOfEmployees' inputType='number'
            inputProps={{ min: 0, integerOnly: true }} />
          <FormRow label='Institutions.BranchOfForeignBank' name='branchOfForeignBank' inputType='checkbox' />
        </PermissionSectionProvider>
      </Col>
      <Col lg='6'>
        <PermissionSectionProvider institutionPermission={institutionPermissionTypes.enum.CompanyDetail}>
          <FormRow label='Institutions.Code' name='code' readonly />
          <FormRow label='Institutions.SequenceNumber' name='sequenceNumber' readonly />
          <FormRow label='Common.ArabicName' name='name' required inputProps={{ maxLength: 150 }} />
          <FormRow label='Common.LatinName' name='latinName' required inputProps={{ maxLength: 150 }} />
          <FormRow label='Institutions.OwnershipType' name='ownershipType' inputType='lookup' dataSource={context.cache.getOwnershipTypes} clientSide isCodeList required />
          <FormRow label='Institutions.PropertyType' name='propertyType' inputType='lookup' dataSource={context.cache.getPropertyTypes} clientSide isCodeList required />
          <FormRow label='Institutions.IncorporationCode' name='incorporationCode' inputProps={{ maxLength: 100 }} />
          <FormRow label='Institutions.IncorporationState' name='incorporationState' inputType='lookup' dataSource={context.cache.getIncorporationStates} clientSide isCodeList />
        </PermissionSectionProvider>
        <PermissionSectionProvider institutionPermission={institutionPermissionTypes.enum.CompanyDealsWithBank}>
          <FieldSet>
            <FormRow label='Institutions.DealsWithBank' name='dealsWithBank' inputType='dropdown' dataSource={context.api.bankService.getLookupAll} onChange={onDealsWithBankChange} />
            <FormRow label='Common.ApprovalDate' name='dealsWithBankApprovalDate' inputType='date' required={!!dealsWithBank} readonly={!dealsWithBankChanged} />
            <FormRow label='Common.ValidFrom' name='dealsWithBankValidFrom' inputType='date' required={!!dealsWithBank} readonly={!dealsWithBankChanged} />
            <FormRow label='Common.ChangeNumber' name='dealsWithBankChangeNumber' inputProps={{ maxLength: 32 }} readonly={!dealsWithBankChanged} />
            <FormRow label='Common.ChangeDate' name='dealsWithBankChangeDate' inputType='date' readonly={!dealsWithBankChanged} />
          </FieldSet>
        </PermissionSectionProvider>
      </Col>
      <Col>
        <PermissionSectionProvider institutionPermission={institutionPermissionTypes.enum.CompanyDetail}>
          <InfoRow form>
            <InfoLabel><Resx name='Common.Description' /></InfoLabel>
            <InfoContent>
              <PropertyRenderer name='description' inputType='textarea' inputProps={{ rows: 10 }} />
            </InfoContent>
          </InfoRow>
        </PermissionSectionProvider>
      </Col>
    </Row>
  )
}



// WEBPACK FOOTER //
// ./src/components/demographics/companyBasicDetails.js